import React, { useState } from 'react'
import { Box, TextField, IconButton, Chip, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { Field, useForm, useFormState } from 'react-final-form'

export default function AiPoweredEmailField() {
  const { values } = useFormState()
  const form = useForm()
  const [inputValue, setInputValue] = useState('')

  const services = values?.metadata?.services || []

  const handleAddService = () => {
    if (inputValue.trim()) {
      const currentServices = services
      const newServices = [...currentServices, inputValue.trim()]

      form.change('metadata', {
        ...values.metadata,
        services: newServices
      })

      setInputValue('')
    }
  }

  const handleRemoveService = indexToRemove => {
    const currentServices = services
    const newServices = currentServices.filter((_, index) => index !== indexToRemove)

    form.change('metadata', {
      ...values.metadata,
      services: newServices
    })
  }

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleAddService()
    }
  }

  return (
    <Box display={'flex'} flexDirection={'column'} rowGap={3} pt={2}>
      <Box>
        <Typography
          sx={{
            mb: 1,
            color: '#76767F',
            fontWeight: 500,
            fontSize: '14px',
            fontFamily: 'Plus Jakarta Sans'
          }}
        >
          Add Service
        </Typography>
        <Box display={'flex'} gap={1} alignItems={'center'}>
          <TextField
            fullWidth
            placeholder='Enter service name'
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            sx={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              '& .MuiInputBase-root': {
                borderRadius: '12px',
                fontSize: '14px',
                fontFamily: 'Plus Jakarta Sans'
              },
              '& .MuiInputBase-input': {
                padding: '12px 14px'
              }
            }}
          />
          <IconButton
            onClick={handleAddService}
            disabled={!inputValue.trim()}
            sx={{
              backgroundColor: inputValue.trim() ? '#0246F8' : '#E5E5E5',
              color: inputValue.trim() ? 'white' : '#76767F',
              borderRadius: '8px',
              width: '44px',
              height: '44px',
              '&:hover': {
                backgroundColor: inputValue.trim() ? '#0235D6' : '#E5E5E5'
              },
              '&.Mui-disabled': {
                backgroundColor: '#E5E5E5',
                color: '#76767F'
              }
            }}
          >
            <AddIcon fontSize='small' />
          </IconButton>
        </Box>
      </Box>

      {services.length > 0 && (
        <Box>
          <Typography
            sx={{
              mb: 2,
              color: '#76767F',
              fontWeight: 500,
              fontSize: '14px',
              fontFamily: 'Plus Jakarta Sans'
            }}
          >
            Selected Services ({services.length})
          </Typography>
          <Box
            display={'flex'}
            flexWrap={'wrap'}
            gap={1}
            sx={{
              maxHeight: '200px',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '4px'
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px'
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '4px'
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a8a8a8'
              }
            }}
          >
            {services.map((service, index) => (
              <Chip
                key={index}
                label={service}
                onDelete={() => handleRemoveService(index)}
                deleteIcon={<CloseIcon fontSize='small' />}
                sx={{
                  backgroundColor: '#F8F9FD',
                  color: '#2D3748',
                  borderRadius: '8px',
                  fontFamily: 'Plus Jakarta Sans',
                  fontSize: '13px',
                  fontWeight: 500,
                  '& .MuiChip-deleteIcon': {
                    color: '#76767F',
                    fontSize: '16px',
                    '&:hover': {
                      color: '#FF4757'
                    }
                  },
                  '&:hover': {
                    backgroundColor: '#EAECF7'
                  }
                }}
              />
            ))}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          backgroundColor: '#0246F80D',
          borderRadius: '12px',
          padding: '12px 16px',
          display: 'flex',
          gap: '12px'
        }}
      >
        <svg
          style={{
            flexShrink: 0
          }}
          width='24'
          height='24'
          viewBox='0 0 24 24'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path opacity='0.2' d='M20.25 15H15V20.2507L20.25 15Z' fill='#0246F8' />
          <path d='M9 9H15' stroke='#0246F8' strokeLinecap='round' strokeLinejoin='round' />
          <path d='M9 12H15' stroke='#0246F8' strokeLinecap='round' strokeLinejoin='round' />
          <path d='M9 15H12' stroke='#0246F8' strokeLinecap='round' strokeLinejoin='round' />
          <path
            d='M14.6893 20.25H4.5C4.30109 20.25 4.11032 20.171 3.96967 20.0303C3.82902 19.8897 3.75 19.6989 3.75 19.5V4.5C3.75 4.30109 3.82902 4.11032 3.96967 3.96967C4.11032 3.82902 4.30109 3.75 4.5 3.75H19.5C19.6989 3.75 19.8897 3.82902 20.0303 3.96967C20.171 4.11032 20.25 4.30109 20.25 4.5V14.6893C20.25 14.7878 20.2306 14.8854 20.1929 14.9764C20.1552 15.0673 20.1 15.15 20.0303 15.2197L15.2197 20.0303C15.15 20.1 15.0673 20.1552 14.9764 20.1929C14.8854 20.2306 14.7878 20.25 14.6893 20.25V20.25Z'
            stroke='#0246F8'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <path d='M20.1825 15H15V20.1828' stroke='#0246F8' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
        <Typography variant='body2' color='#0246F8' fontWeight={500}>
          Choose your services for simulation
        </Typography>
      </Box>

      <Field name='metadata.services' render={() => null} />
    </Box>
  )
}
